import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaCalendarAlt,
  FaTags,
  FaNewspaper,
  FaLayerGroup,
} from "react-icons/fa";
import DOMPurify from "dompurify";
import ButtonEdit from "../../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../../components/Button/ButtonDelete";
import useScrollToTop from "../../../../hooks/useScrollToTop";
import { ToastContainer, toast } from "react-toastify";
import {
  getAllUpdates,
  deleteUpdateById,
} from "../../../../services/updatesServices";
import AddUpdateModal from "./AddUpdateModal";
import EditUpdateModal from "./EditUpdateModal";

const CARD =
  "rounded-[26px] border border-slate-200/80 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 shadow-[0_18px_50px_-20px_rgba(15,23,42,0.28)] backdrop-blur";

const HERO =
  "relative overflow-hidden rounded-[30px] bg-slate-950 text-white border border-slate-800 shadow-[0_25px_70px_-25px_rgba(15,23,42,0.85)]";

const INPUT =
  "w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/40";

const BUTTON_SOFT =
  "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-medium transition";

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function htmlToPlainText(html = "") {
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });

  return clean.replace(/\s+/g, " ").trim();
}

function sanitizeHtml(html = "") {
  return DOMPurify.sanitize(html);
}

function truncate(str = "", max = 150) {
  if (!str) return "—";
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
}

function StatCard({ icon, label, value, hint, dark = false }) {
  return (
    <div
      className={
        dark
          ? "rounded-[22px] border border-white/15 bg-white/10 p-4 backdrop-blur-md"
          : "rounded-[22px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm"
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div
            className={`text-xs uppercase tracking-[0.18em] ${
              dark ? "text-white/60" : "text-slate-500 dark:text-slate-400"
            }`}
          >
            {label}
          </div>

          <div
            className={`mt-2 text-2xl font-semibold ${
              dark ? "text-white" : "text-slate-900 dark:text-slate-50"
            }`}
          >
            {value}
          </div>

          {hint ? (
            <div
              className={`mt-1 text-xs ${
                dark ? "text-white/70" : "text-slate-500 dark:text-slate-400"
              }`}
            >
              {hint}
            </div>
          ) : null}
        </div>

        <div
          className={`rounded-2xl p-3 ${
            dark
              ? "bg-white/10 text-white"
              : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
          }`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, description }) {
  return (
    <div className="mb-5">
      {eyebrow ? (
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-500">
          {eyebrow}
        </div>
      ) : null}

      <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-50">
        {title}
      </h2>

      {description ? (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {description}
        </p>
      ) : null}
    </div>
  );
}

function Badge({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
    green:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    purple:
      "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300",
    orange:
      "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
    pink: "bg-pink-100 text-pink-700 dark:bg-pink-500/15 dark:text-pink-300",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
        tones[tone] || tones.gray
      }`}
    >
      {children}
    </span>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td
      className={`px-4 py-4 align-top text-sm text-slate-700 dark:text-slate-200 ${className}`}
    >
      {children}
    </td>
  );
}

function EmptyState() {
  return (
    <div className="p-10 text-center">
      <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-2xl dark:bg-slate-800">
        🗞️
      </div>

      <div className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        Aucune mise à jour trouvée
      </div>

      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Ajoute une mise à jour ou élargis ta recherche.
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-3 p-5">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
        />
      ))}
    </div>
  );
}

function MobileInfo({ label, value }) {
  return (
    <div className="rounded-2xl bg-white p-3 dark:bg-slate-900">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </div>

      <div className="mt-1 break-words font-medium text-slate-900 dark:text-slate-50">
        {value}
      </div>
    </div>
  );
}

const UpdatesPage = () => {
  useScrollToTop();

  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUpdateId, setEditUpdateId] = useState(null);

  const fetchUpdates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllUpdates();
      setUpdates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des mises à jour :", error);
      toast.error("Erreur lors du chargement des mises à jour");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUpdates();
  }, [fetchUpdates]);

  const tagOptions = useMemo(() => {
    const set = new Set();

    updates.forEach((update) => {
      if (update.tag_update) set.add(update.tag_update);
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [updates]);

  const filteredUpdates = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    let rows = [...updates];

    if (term) {
      rows = rows.filter((update) => {
        const haystack = [
          update.title_update,
          htmlToPlainText(update.content_update || ""),
          update.tag_update,
          formatDate(update.date_update),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(term);
      });
    }

    if (selectedTag) {
      rows = rows.filter((update) => update.tag_update === selectedTag);
    }

    rows.sort((a, b) => {
      switch (sortBy) {
        case "date_asc":
          return new Date(a.date_update || 0) - new Date(b.date_update || 0);

        case "title_asc":
          return String(a.title_update || "").localeCompare(
            String(b.title_update || ""),
            "fr"
          );

        case "title_desc":
          return String(b.title_update || "").localeCompare(
            String(a.title_update || ""),
            "fr"
          );

        case "tag_asc":
          return String(a.tag_update || "").localeCompare(
            String(b.tag_update || ""),
            "fr"
          );

        case "date_desc":
        default:
          return new Date(b.date_update || 0) - new Date(a.date_update || 0);
      }
    });

    return rows;
  }, [updates, searchTerm, selectedTag, sortBy]);

  const stats = useMemo(() => {
    const withTag = filteredUpdates.filter((update) => !!update.tag_update).length;

    const tagsCount = new Set(
      filteredUpdates.map((update) => update.tag_update).filter(Boolean)
    ).size;

    const latest = filteredUpdates.length
      ? [...filteredUpdates].sort(
          (a, b) => new Date(b.date_update || 0) - new Date(a.date_update || 0)
        )[0]
      : null;

    return {
      total: filteredUpdates.length,
      withTag,
      tagsCount,
      latestDate: latest ? formatDate(latest.date_update) : "—",
    };
  }, [filteredUpdates]);

  const deleteUpdate = async (id) => {
    if (!window.confirm(`Supprimer cette mise à jour ? ID: ${id}`)) {
      return;
    }

    try {
      await deleteUpdateById(id);
      toast.success("Mise à jour supprimée avec succès");
      setUpdates((prev) => prev.filter((update) => update.id !== id));
    } catch (error) {
      console.error("Erreur suppression update :", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const openEditModal = (id) => {
    setEditUpdateId(id);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditUpdateId(null);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedTag("");
    setSortBy("date_desc");
  };

  const isEmpty = !loading && filteredUpdates.length === 0;

  return (
    <div className="space-y-6 p-4 lg:p-6">
      <section className={HERO}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(129,140,248,0.35),transparent_30%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.30),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(34,197,94,0.16),transparent_30%)]" />

        <div className="relative p-6 md:p-8">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-white/80">
                <FaNewspaper />
                Roadmap & nouveautés
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                Mises à jour des sites
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/75 md:text-base">
                Centralise les évolutions de tes applications, annonces,
                corrections, nouveautés et changements visibles par tes clients.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className={`${BUTTON_SOFT} bg-white text-slate-900 hover:bg-slate-100`}
              >
                <FaPlus />
                Ajouter une mise à jour
              </button>

              <button
                type="button"
                onClick={fetchUpdates}
                className={`${BUTTON_SOFT} border border-white/15 bg-white/10 text-white hover:bg-white/20`}
              >
                <FaSyncAlt />
                Rafraîchir
              </button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatCard
              dark
              icon={<FaNewspaper />}
              label="Mises à jour"
              value={stats.total}
              hint="Résultats affichés"
            />

            <StatCard
              dark
              icon={<FaTags />}
              label="Avec tag"
              value={stats.withTag}
              hint="Classées par catégorie"
            />

            <StatCard
              dark
              icon={<FaLayerGroup />}
              label="Tags"
              value={stats.tagsCount}
              hint="Tags différents"
            />

            <StatCard
              dark
              icon={<FaCalendarAlt />}
              label="Dernière"
              value={stats.latestDate}
              hint="Mise à jour récente"
            />
          </div>
        </div>
      </section>

      <section className={`${CARD} p-5 md:p-6`}>
        <SectionHead
          eyebrow="Filtres"
          title="Retrouver rapidement une mise à jour"
          description="Recherche par titre, contenu, tag ou date."
        />

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
          <div className="relative xl:col-span-6">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              className={`${INPUT} pl-11`}
              type="text"
              placeholder="Rechercher une mise à jour..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="xl:col-span-2">
            <select
              className={INPUT}
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
            >
              <option value="">Tous les tags</option>
              {tagOptions.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </div>

          <div className="xl:col-span-3">
            <select
              className={INPUT}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date_desc">Tri : Plus récentes</option>
              <option value="date_asc">Tri : Plus anciennes</option>
              <option value="title_asc">Tri : Titre A → Z</option>
              <option value="title_desc">Tri : Titre Z → A</option>
              <option value="tag_asc">Tri : Tag</option>
            </select>
          </div>

          <div className="xl:col-span-1">
            <button
              type="button"
              onClick={resetFilters}
              className="w-full rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Reset
            </button>
          </div>
        </div>
      </section>

      <section className={`${CARD} overflow-hidden`}>
        <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800 md:px-6">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                Journal des mises à jour
              </h2>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Liste complète des annonces, corrections et nouveautés.
              </p>
            </div>

            <Badge tone="purple">{filteredUpdates.length} affichée(s)</Badge>
          </div>
        </div>

        {loading ? (
          <SkeletonList />
        ) : isEmpty ? (
          <EmptyState />
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full min-w-[1180px]">
                <thead className="bg-slate-50 dark:bg-slate-800/60">
                  <tr>
                    <Th>Date</Th>
                    <Th>Titre</Th>
                    <Th>Contenu</Th>
                    <Th>Tag</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>

                <tbody>
                  {filteredUpdates.map((update) => (
                    <tr
                      key={update.id}
                      className="border-b border-slate-100 transition hover:bg-slate-50/70 dark:border-slate-800 dark:hover:bg-slate-800/30"
                    >
                      <Td className="min-w-[150px]">
                        <div className="font-semibold text-slate-900 dark:text-slate-50">
                          {formatDate(update.date_update)}
                        </div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          #{update.id}
                        </div>
                      </Td>

                      <Td className="min-w-[240px]">
                        <div className="font-semibold text-slate-900 dark:text-slate-50">
                          {update.title_update || "Sans titre"}
                        </div>
                      </Td>

                      <Td className="min-w-[420px] max-w-[520px]">
                        <div
                          className="prose prose-sm max-w-none leading-6 text-slate-600 dark:prose-invert dark:text-slate-300 line-clamp-4"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(update.content_update || ""),
                          }}
                        />
                      </Td>

                      <Td className="min-w-[160px]">
                        <Badge tone="blue">{update.tag_update || "—"}</Badge>
                      </Td>

                      <Td className="min-w-[120px]">
                        <div className="flex justify-end gap-2">
                          <ButtonEdit
                            onClick={() => openEditModal(update.id)}
                            hoverLabel="Modifier"
                          />
                          <ButtonDelete
                            onClick={() => deleteUpdate(update.id)}
                            hoverLabel="Supprimer"
                          />
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-4 p-4 md:hidden">
              {filteredUpdates.map((update) => (
                <div
                  key={update.id}
                  className="rounded-[24px] border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-800/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 dark:text-slate-50">
                        {update.title_update || "Sans titre"}
                      </div>

                      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        #{update.id} · {formatDate(update.date_update)}
                      </div>
                    </div>

                    <Badge tone="blue">{update.tag_update || "—"}</Badge>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3">
                    <MobileInfo
                      label="Contenu"
                      value={truncate(
                        htmlToPlainText(update.content_update || ""),
                        180
                      )}
                    />
                    <MobileInfo label="Date" value={formatDate(update.date_update)} />
                  </div>

                  <div className="mt-4 flex justify-end gap-2">
                    <ButtonEdit
                      onClick={() => openEditModal(update.id)}
                      hoverLabel="Modifier"
                    />
                    <ButtonDelete
                      onClick={() => deleteUpdate(update.id)}
                      hoverLabel="Supprimer"
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <AddUpdateModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        refreshList={fetchUpdates}
      />

      {showEditModal && editUpdateId && (
        <EditUpdateModal
          isOpen={showEditModal}
          onClose={closeEditModal}
          updateId={editUpdateId}
          refreshList={fetchUpdates}
        />
      )}

      <ToastContainer />
    </div>
  );
};

export default UpdatesPage;